<template>
    <nav
        class="py-2 select-none"
        aria-label="Filter"
    >
        <div
            class="wrapper flex w-full justify-end rounded-md p-0.5 shadow-sm ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-blue-600 focus:ring-inset"
        >
            <div :class="[tags.show ? 'w-1/2' : 'w-full', 'flex']">
                <input
                    v-show="search.show"
                    id="search"
                    v-model="search.subject"
                    type="text"
                    name="search"
                    class="mr-1 block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    :placeholder="t('search')"
                    @keyup="triggerFilter()"
                    @change="triggerFilter()"
                    @input="triggerFilter()"
                />
                <button
                    v-show="search.show"
                    v-tippy="search.title"
                    type="button"
                    class="relative inline-flex cursor-pointer items-center justify-end bg-white px-2 py-2 text-gray-400 shadow-sm ring-0 hover:bg-gray-200 hover:text-gray-600"
                    @click.prevent="triggerFilter()"
                >
                    <MdiMagnify
                        class="h-5 w-5"
                        aria-hidden="true"
                    />
                    <span class="sr-only">{{ search.title }}</span>
                </button>
            </div>

            <div
                v-if="tags.show"
                class="flex w-1/2"
                @click.stop
            >
                <div class="w-full">
                    <input
                        id="tag-search"
                        v-model="tags.subject"
                        type="text"
                        name="tag-search"
                        class="mr-1 w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:ring-offset-0 sm:text-sm sm:leading-6"
                        :placeholder="t('tagEnter')"
                        @blur="blur"
                        @click="filterSuggestions()"
                        @keyup="filterSuggestions()"
                    />
                    <div class="relative w-full">
                        <Transition
                            :show="filterConfig.showSuggestions"
                            enter-active-class="duration-300 ease-out"
                            enter-from-class="transform opacity-0"
                            enter-to-class="opacity-100"
                            leave-active-class="duration-200 ease-in"
                            leave-from-class="opacity-100"
                            leave-to-class="transform opacity-0"
                        >
                            <div
                                v-if="filterConfig.showSuggestions"
                                class="absolute top-px z-3000 m-0 flex w-full flex-wrap items-start gap-2 rounded-b-md border border-solid border-gray-300 bg-white p-4"
                            >
                                <div
                                    v-for="(tag, index) in filterConfig.suggested"
                                    :key="index"
                                    v-tippy="t('buttonAddTag')"
                                    class="my-1 inline-block"
                                    @mousedown="addTag(tag)"
                                >
                                    <span
                                        class="cursor-pointer rounded-sm px-2 py-0.5 text-xs font-medium hover:opacity-75"
                                        :style="getTagStyles(tag)"
                                    >
                                        {{ tag.name }}
                                    </span>
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>
            </div>

            <div
                v-for="(item, index) in buttons"
                v-show="item.show"
                :key="index"
                class="flex-none rounded-md border-0"
            >
                <button
                    v-tippy="item.title"
                    type="button"
                    :class="[
                        index === buttons.length - 1 ? 'rounded-r-sm' : 'rounded-none',
                        'relative inline-flex cursor-pointer items-center justify-end bg-white px-2 py-2 text-gray-400 ring-0 hover:bg-gray-200 hover:text-gray-600',
                    ]"
                    @click="item.action"
                >
                    <component
                        :is="item.icon"
                        class="h-5 w-5 shrink-0"
                        aria-hidden="true"
                    />
                    <span class="sr-only">{{ item.title }}</span>
                </button>
            </div>
        </div>
        <div class="flex flex-wrap items-start gap-2 py-2">
            <div
                v-for="tag in filterConfig.usedTags"
                :key="tag.tagId"
                class="flex cursor-default items-center justify-center rounded-sm bg-blue-600 px-1.5 py-0.5 text-xs font-medium text-gray-50"
                :style="getTagStyles(tag)"
            >
                <span class="-mt-px">{{ tag.name }}</span>
                <button
                    v-tippy="t('buttonRemoveTag')"
                    class="ml-2 cursor-pointer border-none hover:opacity-75"
                    @click.stop="removeTag(tag)"
                >
                    <MdiClose class="h-3 w-3" />
                </button>
            </div>
        </div>
    </nav>
</template>

<script setup>
    import { reactive, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { setTextBrightness } from '../composables/useColor';
    import { MdiMagnify, MdiClose } from 'materialdesignicons-vue3/icons/';

    defineExpose({
        name: 'ListToolbar',
    });

    const props = defineProps({
        indexId: {
            type: String,
            default: 'id',
        },
        childKey: {
            type: String,
            default: '',
        },
        search: {
            type: Object,
            default: () => {
                return {
                    subject: '',
                    keys: ['name'],
                    show: false,
                    trigger: false,
                };
            },
        },
        tags: {
            type: Object,
            default: () => {
                return {
                    show: false,
                    subject: '',
                    all: [],
                    selected: [],
                };
            },
        },
        buttons: {
            type: Object,
            default: () => {
                return [];
            },
        },
        data: {
            type: Object,
            default: () => {
                return [];
            },
        },
        result: {
            type: Object,
            default: () => {
                return [];
            },
        },
    });

    const data = props.data;
    const result = props.result;
    const search = props.search;
    const tags = props.tags;
    const buttons = props.buttons;
    const indexId = props.indexId;
    const childKey = props.childKey;
    const router = useRouter();

    const filterConfig = reactive({
        showSuggestions: false,
        suggested: [],
        usedTags: [],
        all: tags.all,
        results: {
            fulltext: [],
            tagSearch: [],
        },
        searchLengthMin: 2,
    });

    const transformUsedTags = () => {
        filterConfig.usedTags = tags.selected
            .map((tagStr) => tags.all.find((tag) => tagStr === tag.name))
            .filter(Boolean);
    };

    transformUsedTags();

    const blur = () => {
        filterConfig.showSuggestions = false;
    };

    const filterSuggestions = () => {
        filterConfig.showSuggestions = true;
        filterConfig.suggested = filterConfig.all.filter((item) =>
            item.name.toLowerCase().includes(tags.subject.toLowerCase()),
        );
    };

    const removeTag = async (tag) => {
        if (isUsedTag(tag)) {
            filterConfig.usedTags = filterConfig.usedTags.filter((item) => {
                return tag.tagId != item.tagId;
            });
            await triggerFilter();
        }
    };

    const addTag = async (tag) => {
        if (!isUsedTag(tag)) {
            filterConfig.usedTags.push(tag);
            await triggerFilter();
        }
    };

    const isUsedTag = (tag) => {
        return filterConfig.usedTags.some((item) => item.tagId === tag.tagId);
    };

    const isItemTag = (item, tag) => {
        return item.extension.TAGS.some((itemTag) => itemTag.tag.tagId === tag.tagId);
    };

    const itemIsFoundIn = (item, inkey) => {
        return filterConfig.results[inkey].some((itemIn) => {
            return childKey.length > 0
                ? itemIn[childKey]?.[indexId] === item[childKey]?.[indexId]
                : itemIn[indexId] === item[indexId];
        });
    };

    const tagSearch = async () => {
        if (filterConfig.usedTags.length === 0) {
            filterConfig.results.tagSearch = data;
            return;
        }
        filterConfig.results.tagSearch = data.filter((item) =>
            filterConfig.usedTags.every((tag) => isItemTag(item, tag)),
        );
    };

    const fulltext = async () => {
        if (search.subject.length <= filterConfig.searchLengthMin) {
            filterConfig.results.fulltext = data;
            return;
        }

        filterConfig.results.fulltext = data.filter((item) => {
            return search.keys.some((key) => {
                const value = childKey.length > 0 ? item[childKey]?.[key] : item[key];
                return (
                    typeof value === 'string' &&
                    value.toLowerCase().includes(search.subject.toLowerCase())
                );
            });
        });
    };

    const finalSearchResult = () => {
        result.length = 0;
        data.map((item) => {
            const hide = !(itemIsFoundIn(item, 'tagSearch') && itemIsFoundIn(item, 'fulltext'));
            const resultItem = toggleObject(item, hide);
            if (hide === false) {
                result.push(resultItem);
            }
        });
        search.trigger = false;
    };

    const toggleObject = (item, hide) => {
        if (childKey.length > 0) {
            item[childKey]._hide = hide;
        } else {
            item._hide = hide;
        }
        return item;
    };

    const getTagStyles = (tag) => {
        const styles = {
            backgroundColor: tag.color || '#000000',
            borderColor: tag.color || '#000000',
            color: setTextBrightness(tag.color || '#000000', true),
        };
        return `color: ${styles.color}; background-color: ${styles.backgroundColor}; border-color: ${styles.borderColor};`;
    };

    const getFilterQuery = () => {
        const q = {
            search: [],
            tag: [],
        };
        if (search.subject.length > filterConfig.searchLengthMin) {
            q.search.push(search.subject);
        }
        if (filterConfig.usedTags.length > 0) {
            filterConfig.usedTags.forEach((tag) => {
                q.tag.push(tag.name);
            });
        }
        return q;
    };

    const triggerFilter = async () => {
        await fulltext();
        await tagSearch();
        finalSearchResult();
    };

    watch(
        filterConfig,
        () => {
            const newQuery = getFilterQuery();
            router.replace({
                query: newQuery,
            });
        },
        { deep: true },
    );

    watch(search, () => {
        if (search.trigger === true) {
            triggerFilter();
        }
    });

    triggerFilter();
</script>

<style>
    .slide-fade-enter-active {
        transition: all 200ms ease-out;
    }

    .slide-fade-leave-active {
        transition: all 200ms ease-in;
    }

    .slide-fade-enter-from,
    .slide-fade-leave-to {
        transform: translateY(100px);
        opacity: 0;
    }
</style>
